import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import attendanceService from '../../services/attendanceService';

export const markAttendance = createAsyncThunk(
  'attendance/markAttendance',
  async ({ latitude, longitude, attendanceType }, { rejectWithValue }) => {
    try {
      const response = await attendanceService.mark(latitude, longitude, attendanceType);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark attendance');
    }
  }
);

export const fetchAttendance = createAsyncThunk(
  'attendance/fetchAttendance',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await attendanceService.getAll(page, limit, filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch attendance');
    }
  }
);

export const fetchEmployeeAttendance = createAsyncThunk(
  'attendance/fetchEmployeeAttendance',
  async ({ employeeId, month }, { rejectWithValue }) => {
    try {
      const response = await attendanceService.getEmployeeAttendance(employeeId, month);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch employee attendance');
    }
  }
);

export const verifyAttendance = createAsyncThunk(
  'attendance/verifyAttendance',
  async ({ id, isVerified, verificationNotes }, { rejectWithValue }) => {
    try {
      const response = await attendanceService.verify(id, isVerified, verificationNotes);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to verify attendance');
    }
  }
);

const initialState = {
  records: [],
  currentStatus: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0
  },
  isLoading: false,
  error: null
};

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Mark attendance
    builder
      .addCase(markAttendance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(markAttendance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentStatus = action.payload;
      })
      .addCase(markAttendance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch attendance
    builder
      .addCase(fetchAttendance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAttendance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.records = action.payload;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAttendance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch employee attendance
    builder
      .addCase(fetchEmployeeAttendance.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchEmployeeAttendance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.records = action.payload;
      })
      .addCase(fetchEmployeeAttendance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Verify attendance
    builder
      .addCase(verifyAttendance.fulfilled, (state, action) => {
        const index = state.records.findIndex(record => record.id === action.payload.id);
        if (index !== -1) {
          state.records[index] = action.payload;
        }
      });
  }
});

export const { clearError } = attendanceSlice.actions;
export default attendanceSlice.reducer;
